/**
 * @file The Brewery Tour's booking card (sections/tour-details.liquid), the phone's bar back to
 * it, and the hero's Next tour (sections/main-product-tour.liquid).
 *
 * Evey Events & Tickets takes the bookings. The card reads the tour's dates and the places left on
 * each from Evey's storefront schedule, the same one Evey's own date picker reads, and offers them as
 * a row of dates to pick from. The tickets are the product's options. Book now adds the tickets to
 * the cart marked the way Evey's picker marks them: an _event_id property of the tour's start,
 * schedule item and end ("1793617200|73562|1793620800"), which is what Evey issues the tickets from,
 * and an Event property naming the date ("Monday, November 2, 2026 at 11 AM - 12 PM"), which shows
 * in the cart and the order. Evey keeps its times in the tour's own clock written as UTC, so they're
 * shown here in UTC too, which reads them as Beechworth time.
 *
 * Every ticket counts towards a tour's places, a free child's too, as in Evey. Tickets for the same
 * tour already in the cart count as well, so Book now never asks for more places than are left.
 */

import { openDrawer } from './drawer.js';
import { updateUI as updateCartUI } from './cartDrawer.js';
import { formatMoney } from './productForm.js';

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const SEL = {
	card: '[data-tour-booking]',
	dates: '[data-tour-dates]',
	times: '[data-tour-times]',
	whenNote: '[data-tour-when-note]',
	ticket: '[data-tour-ticket]',
	change: '[data-tour-change]',
	count: '[data-tour-count]',
	places: '[data-tour-places]',
	total: '[data-tour-total]',
	submit: '[data-tour-submit]',
	submitLabel: '[data-tour-submit-label]',
	error: '[data-tour-error]',
	loader: '.loader',
	next: '[data-tour-next]',
	nextWhen: '[data-tour-next-when]',
	phoneBar: '[data-tour-phone-bar]',
	hero: '[data-tour-hero]',
	drawer: '[data-cart-drawer]',
};

/* ----------------------------------------------------------------------- */
/*  Rules                                                                  */
/* ----------------------------------------------------------------------- */

/** Evey's storefront schedule, which its own date picker reads. */
const EVEY_SCHEDULE = 'https://api.eveyevents.com/production-v2/storefront/schedule';

/** The brewery's clock, for today's date. */
const TIME_ZONE = 'Australia/Melbourne';

/** How many days of tours are read at a time; More dates reads the next lot. */
const DAYS_AT_A_TIME = 56;

/** How many lots More dates reads at most, looking past a break with no tours, before it gives up. */
const MOST_LOTS = 3;

/** From this many places left, the card says how many. */
const FEW_PLACES = 10;

/** The most tickets of one kind the card offers before a date is picked; after, the tour's places are the limit. */
const MOST_TICKETS = 50;

/** The property naming the tour's date on each ticket, as Evey names it. */
const EVENT_PROPERTY = 'Event';

/* ----------------------------------------------------------------------- */
/*  Dates                                                                  */
/* ----------------------------------------------------------------------- */

/**
 * The date and time now at the brewery, written the way Evey writes a tour's start.
 *
 * @returns {string} Like 2026-10-15T13:52.
 */
function breweryNow() {
	const parts = {};
	new Intl.DateTimeFormat('en-AU', {
		timeZone: TIME_ZONE,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	})
		.formatToParts(new Date())
		.forEach((part) => {
			parts[part.type] = part.value;
		});
	return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * Today's date at the brewery.
 *
 * @returns {string} Like 2026-10-15.
 */
function today() {
	return breweryNow().slice(0, 10);
}

/**
 * A date some days on.
 *
 * @param {string} day - Like 2026-10-15.
 * @param {number} days
 * @returns {string}
 */
function addDays(day, days) {
	const date = new Date(`${day}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + days);
	return date.toISOString().slice(0, 10);
}

/**
 * Part of a tour's date, as the brewery's clock shows it.
 *
 * @param {Date} date
 * @param {Intl.DateTimeFormatOptions} options
 * @returns {string}
 */
function dateText(date, options) {
	return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: 'UTC' }).format(date);
}

/**
 * A tour's start time, like 11:00am.
 *
 * @param {Date} date
 * @returns {string}
 */
function timeText(date) {
	return dateText(date, { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase();
}

/**
 * A tour's date in a sentence, like Friday 16 October.
 *
 * @param {Date} date
 * @returns {string}
 */
function longDate(date) {
	return dateText(date, { weekday: 'long', day: 'numeric', month: 'long' });
}

/**
 * A tour's date in a few words, like Fri 16 Oct.
 *
 * @param {Date} date
 * @returns {string}
 */
function shortDate(date) {
	return dateText(date, { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
}

/* ----------------------------------------------------------------------- */
/*  Evey                                                                   */
/* ----------------------------------------------------------------------- */

/**
 * The tours from one day to another that are yet to start, earliest first: one each, however many
 * ticket types Evey lists it under. Evey doesn't close a tour's sales when it starts, so a tour
 * that has started is left out here.
 *
 * @param {{ shop: string, productId: string }} config
 * @param {string} from - Like 2026-10-15.
 * @param {string} to
 * @returns {Promise<object[]>}
 */
async function fetchTours(config, from, to) {
	const url = new URL(EVEY_SCHEDULE);
	url.search = new URLSearchParams({
		shop: config.shop,
		productId: config.productId,
		quantity: '0',
		startDate: from,
		endDate: to,
		currentDate: today(),
	}).toString();

	const response = await fetch(url.toString());
	if (!response.ok) {
		throw new Error(`fetchTours: Evey answered ${response.status}`);
	}
	const data = await response.json();
	const listed = data?.schedule?.schedule_events;
	if (!Array.isArray(listed)) {
		throw new Error('fetchTours: Evey sent no schedule');
	}

	const now = breweryNow();
	const tours = new Map();
	listed.forEach((event) => {
		const key = `${event.start}|${event.schedule_item_id}`;
		if (tours.has(key) || String(event.start).slice(0, 16) <= now) {
			return;
		}
		const places = Math.max(0, Number(event.available) || 0);
		tours.set(key, {
			key: key,
			start: event.start,
			end: event.end || null,
			allDay: Boolean(event.all_day),
			scheduleItemId: event.schedule_item_id,
			day: event.start.slice(0, 10),
			places: places,
			open: Boolean(event.is_available) && places > 0,
		});
	});
	return Array.from(tours.values()).sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * The tour's _event_id property, as Evey's picker writes it: its start, schedule item and end.
 *
 * @param {object} tour
 * @returns {string}
 */
function eventId(tour) {
	const start = Math.floor(Date.parse(tour.start) / 1000);
	if (!tour.end) {
		return `${start}|${tour.scheduleItemId}`;
	}
	return `${start}|${tour.scheduleItemId}|${Math.floor(Date.parse(tour.end) / 1000)}`;
}

/**
 * The tour's Event property, as Evey's picker writes it, like
 * "Monday, November 2, 2026 at 11 AM - 12 PM".
 *
 * @param {object} tour
 * @returns {string}
 */
function eventLabel(tour) {
	const locale = window.Shopify?.locale || 'en';
	const dayOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
	const timeOptions = tour.allDay ? {} : { hour: 'numeric', minute: '2-digit' };
	const start = new Intl.DateTimeFormat(locale, { ...dayOptions, ...timeOptions }).format(new Date(tour.start)).replace(/:00/g, '');
	if (tour.allDay || !tour.end) {
		return start;
	}
	const end = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(tour.end)).replace(/:00/g, '');
	return `${start} - ${end}`;
}

/* ----------------------------------------------------------------------- */
/*  Cart                                                                   */
/* ----------------------------------------------------------------------- */

/**
 * How many tickets for a tour are in the cart already.
 *
 * @param {string} id - The tour's _event_id.
 * @returns {Promise<number>}
 */
async function ticketsInCart(id) {
	const response = await fetch('/cart.js', { credentials: 'same-origin' });
	if (!response.ok) {
		throw new Error(`ticketsInCart: the cart answered ${response.status}`);
	}
	const cart = await response.json();
	return cart.items.reduce((sum, item) => {
		return item.properties?._event_id === id ? sum + item.quantity : sum;
	}, 0);
}

/**
 * Add the tickets to the cart.
 *
 * @param {object[]} items
 */
async function addTickets(items) {
	const response = await fetch('/cart/add.js', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify({ items: items }),
	});
	const data = await response.json().catch(() => ({}));
	if (!response.ok) {
		throw new Error(data?.description || data?.message || 'Sorry, those tickets couldn’t be added. Please try again.');
	}
}

/* ----------------------------------------------------------------------- */
/*  The card                                                               */
/* ----------------------------------------------------------------------- */

/**
 * Switch a button off or on. It's marked rather than disabled, so a keyboard's focus stays on it
 * instead of falling back to the top of the page; its click does nothing while it's off.
 *
 * @param {HTMLButtonElement} button
 * @param {boolean} off
 */
function switchOff(button, off) {
	button.disabled = false;
	button.setAttribute('aria-disabled', String(off));
}

/**
 * @param {HTMLButtonElement} button
 * @returns {boolean}
 */
function isOff(button) {
	return button.getAttribute('aria-disabled') === 'true';
}

/**
 * The card's parts, found once.
 *
 * @param {HTMLElement} card
 * @returns {object}
 */
function findParts(card) {
	const parts = {
		card: card,
		dates: card.querySelector(SEL.dates),
		times: card.querySelector(SEL.times),
		whenNote: card.querySelector(SEL.whenNote),
		places: card.querySelector(SEL.places),
		total: card.querySelector(SEL.total),
		submit: card.querySelector(SEL.submit),
		submitLabel: card.querySelector(SEL.submitLabel),
		error: card.querySelector(SEL.error),
		tickets: Array.from(card.querySelectorAll(SEL.ticket)).map((row) => {
			return {
				row: row,
				variantId: row.dataset.variantId,
				price: Number(row.dataset.price) || 0,
				label: row.dataset.label,
				count: row.querySelector(SEL.count),
				less: row.querySelector(`${SEL.change}[data-tour-change="-1"]`),
				more: row.querySelector(`${SEL.change}[data-tour-change="1"]`),
			};
		}),
	};
	const missing = ['dates', 'times', 'whenNote', 'places', 'total', 'submit', 'submitLabel', 'error'].filter((name) => !parts[name]);
	if (missing.length > 0 || parts.tickets.some((ticket) => !ticket.count || !ticket.less || !ticket.more)) {
		throw new Error(`findParts: the booking card is missing ${missing.join(', ') || 'a ticket\'s count or buttons'}`);
	}
	return parts;
}

/**
 * A booking card: its tours, the tour picked and the tickets chosen.
 *
 * @param {HTMLElement} card
 */
function createBooking(card) {
	const parts = findParts(card);
	const config = { shop: card.dataset.shop, productId: card.dataset.productId };
	const state = {
		tours: [],
		readTo: null,
		tour: null,
		counts: new Map(parts.tickets.map((ticket) => [ticket.variantId, 0])),
		busy: false,
	};

	const totalTickets = () => Array.from(state.counts.values()).reduce((sum, count) => sum + count, 0);

	const toursOn = (day) => state.tours.filter((tour) => tour.day === day);

	/* The time every tour starts, or null when they differ. */
	const sharedTime = () => {
		const times = new Set(state.tours.map((tour) => (tour.allDay ? 'all day' : timeText(new Date(tour.start)))));
		return times.size === 1 ? [...times][0] : null;
	};

	/* The note under the dates: the time every tour starts, or how to pick one. */
	const renderWhenNote = () => {
		const open = state.tours.filter((tour) => tour.open);
		if (state.tours.length === 0) {
			parts.whenNote.textContent = 'There are no tours to book right now. Contact us and we’ll let you know when there are.';
			return;
		}
		if (open.length === 0) {
			parts.whenNote.textContent = 'Every tour in these dates is full. Try More dates.';
			return;
		}
		const time = sharedTime();
		parts.whenNote.textContent = time ? `All tours start at ${time}` : 'Pick a date to see its times';
	};

	/* One date to pick: the day, its date and month, or Full when no tour that day has places. */
	const dateChip = (day) => {
		const tours = toursOn(day);
		const date = new Date(`${day}T00:00:00Z`);
		const open = tours.some((tour) => tour.open);
		const label = document.createElement('label');
		label.className = 'tour-date';

		const input = document.createElement('input');
		input.className = 'sr-only';
		input.type = 'radio';
		input.name = 'tour-date';
		input.value = day;
		input.disabled = !open;
		input.checked = state.tour?.day === day;
		const places = Math.max(...tours.map((tour) => tour.places));
		input.setAttribute(
			'aria-label',
			open ? `${longDate(date)}${places <= FEW_PLACES ? `, ${places} places left` : ''}` : `${longDate(date)}, full`
		);

		const weekday = document.createElement('span');
		weekday.className = 'tour-date__day';
		weekday.textContent = dateText(date, { weekday: 'short' });
		const number = document.createElement('span');
		number.className = 'tour-date__num';
		number.textContent = dateText(date, { day: 'numeric' });
		const month = document.createElement('span');
		month.className = 'tour-date__month';
		month.textContent = open ? dateText(date, { month: 'short' }) : 'Full';

		label.append(input, weekday, number, month);
		return label;
	};

	const daysOf = (tours) => [...new Set(tours.map((tour) => tour.day))];

	const renderDates = () => {
		const more = document.createElement('button');
		more.type = 'button';
		more.className = 'tour-date tour-date--more';
		more.textContent = 'More dates';
		more.addEventListener('click', () => loadMore(more));
		parts.dates.replaceChildren(...daysOf(state.tours).map(dateChip), more);
		parts.dates.removeAttribute('aria-busy');
		markMore();
	};

	/* The row fades at its right edge while there's more of it to scroll to. */
	const markMore = () => {
		const { scrollLeft, scrollWidth, clientWidth } = parts.dates;
		parts.dates.toggleAttribute('data-more', scrollLeft + clientWidth < scrollWidth - 4);
	};

	/* The times to pick from on the day picked: shown when it has more than one tour, or when tours
	   start at different times, so the day's time shows even when it has just the one. */
	const renderTimes = () => {
		const tours = state.tour ? toursOn(state.tour.day) : [];
		if (tours.length === 0 || (tours.length === 1 && sharedTime() !== null)) {
			parts.times.hidden = true;
			parts.times.replaceChildren();
			return;
		}
		parts.times.replaceChildren(...tours.map((tour) => {
			const label = document.createElement('label');
			label.className = 'tour-time';
			const input = document.createElement('input');
			input.className = 'sr-only';
			input.type = 'radio';
			input.name = 'tour-time';
			input.value = tour.key;
			input.disabled = !tour.open;
			input.checked = tour === state.tour;
			label.append(input, tour.open ? timeText(new Date(tour.start)) : `${timeText(new Date(tour.start))} full`);
			return label;
		}));
		parts.times.hidden = false;
	};

	/* The counts, the buttons, the places left, the total and Book now, from the state. */
	const render = () => {
		const total = totalTickets();
		const places = state.tour ? state.tour.places : null;
		const room = places === null ? Infinity : places - total;

		parts.tickets.forEach((ticket) => {
			const count = state.counts.get(ticket.variantId);
			ticket.count.textContent = String(count);
			switchOff(ticket.less, state.busy || count === 0);
			switchOff(ticket.more, state.busy || room <= 0 || (places === null && count >= MOST_TICKETS));
		});

		const cost = parts.tickets.reduce((sum, ticket) => sum + ticket.price * state.counts.get(ticket.variantId), 0);
		parts.total.textContent = formatMoney(cost);

		const tooMany = places !== null && total > places;
		if (tooMany) {
			parts.places.textContent = `Only ${places} ${places === 1 ? 'place is' : 'places are'} left on ${longDate(new Date(state.tour.start))}. Take some tickets off, or pick another date.`;
		} else if (places !== null && places <= FEW_PLACES) {
			parts.places.textContent = `Only ${places} ${places === 1 ? 'place' : 'places'} left on ${longDate(new Date(state.tour.start))}`;
		}
		if (!tooMany && !(places !== null && places <= FEW_PLACES)) {
			// emptied rather than hidden, so screen readers keep listening to it
			parts.places.textContent = '';
		}

		let label = `Book now · ${formatMoney(cost)}`;
		if (!state.tour) {
			label = 'Choose a date';
		} else if (total === 0) {
			label = 'Choose tickets';
		}
		parts.submitLabel.textContent = state.busy ? 'Adding…' : label;
		switchOff(parts.submit, state.busy || !state.tour || total === 0 || tooMany);
	};

	const showError = (message) => {
		parts.error.textContent = message;
		parts.error.hidden = !message;
	};

	/* Pick a date: its first tour with places, and its times if it has more than one. */
	const pickDay = (day) => {
		state.tour = toursOn(day).find((tour) => tour.open) || null;
		showError('');
		renderTimes();
		render();
	};

	const pickTour = (key) => {
		state.tour = state.tours.find((tour) => tour.key === key) || null;
		showError('');
		render();
	};

	/* Read the next lot of dates onto the end of the row, looking a few lots ahead past a break.
	   A keyboard's focus moves on to the first new date it can pick. */
	const loadMore = async (button) => {
		if (isOff(button)) {
			return;
		}
		const focused = document.activeElement === button;
		switchOff(button, true);
		button.textContent = 'Loading…';
		let tours = [];
		try {
			for (let lot = 0; lot < MOST_LOTS && tours.length === 0; lot += 1) {
				const from = addDays(state.readTo, 1);
				const to = addDays(from, DAYS_AT_A_TIME - 1);
				tours = await fetchTours(config, from, to);
				state.readTo = to;
			}
		} catch (err) {
			console.error('[TourBooking] More dates failed:', err);
			switchOff(button, false);
			button.textContent = 'Try again';
			return;
		}

		if (tours.length === 0) {
			const last = state.tours[state.tours.length - 1];
			parts.whenNote.textContent = last
				? `No tours are open for booking after ${longDate(new Date(last.start))} yet.`
				: 'There are no tours to book right now. Contact us and we’ll let you know when there are.';
			if (focused) {
				parts.whenNote.tabIndex = -1;
				parts.whenNote.focus();
			}
			button.remove();
			markMore();
			return;
		}

		state.tours = state.tours.concat(tours);
		const chips = daysOf(tours).map(dateChip);
		button.before(...chips);
		switchOff(button, false);
		button.textContent = 'More dates';
		renderWhenNote();
		markMore();
		const first = chips.map((chip) => chip.querySelector('input')).find((input) => !input.disabled);
		if (focused && first) {
			first.focus();
		}
	};

	const book = async () => {
		const tour = state.tour;
		const total = totalTickets();
		if (!tour || total === 0 || state.busy || isOff(parts.submit)) {
			return;
		}
		const id = eventId(tour);
		state.busy = true;
		parts.submit.setAttribute('aria-busy', 'true');
		parts.submit.querySelector(SEL.loader)?.classList.remove('hide');
		showError('');
		render();

		let added = false;
		try {
			const inCart = await ticketsInCart(id).catch((err) => {
				// If the cart can't be read, let Evey and Shopify decide rather than blocking the booking.
				console.error('[TourBooking] Cart check failed:', err);
				return 0;
			});
			if (inCart + total > tour.places) {
				const left = Math.max(0, tour.places - inCart);
				throw new Error(`Your cart already has ${inCart} ${inCart === 1 ? 'ticket' : 'tickets'} for ${longDate(new Date(tour.start))}, and ${left === 0 ? 'no more places are' : `only ${left} more ${left === 1 ? 'place is' : 'places are'}`} left.`);
			}

			const label = eventLabel(tour);
			const items = parts.tickets
				.filter((ticket) => state.counts.get(ticket.variantId) > 0)
				.map((ticket) => {
					return {
						id: ticket.variantId,
						quantity: state.counts.get(ticket.variantId),
						properties: { _event_id: id, [EVENT_PROPERTY]: label },
					};
				});
			await addTickets(items);
			added = true;
			parts.tickets.forEach((ticket) => state.counts.set(ticket.variantId, 0));
		} catch (err) {
			console.error('[TourBooking] Booking failed:', err);
			showError(err.message);
			// Shopify may take part of a request before refusing the rest, so the cart is refreshed.
			updateCartUI().catch((refreshErr) => {
				console.error('[TourBooking] Cart refresh after a failed booking failed:', refreshErr);
			});
		}

		if (added) {
			// The tickets are in the cart now: if the drawer can't show them, the cart page does.
			try {
				await updateCartUI();
				openDrawer(document.querySelector(SEL.drawer));
			} catch (err) {
				console.error('[TourBooking] The cart drawer couldn’t show the tickets, so on to the cart:', err);
				window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
			}
		}

		state.busy = false;
		parts.submit.removeAttribute('aria-busy');
		parts.submit.querySelector(SEL.loader)?.classList.add('hide');
		render();
	};

	const load = async () => {
		const retrying = parts.dates.contains(document.activeElement);
		parts.whenNote.textContent = 'Finding the next tours…';
		try {
			const from = today();
			const to = addDays(from, DAYS_AT_A_TIME - 1);
			state.tours = await fetchTours(config, from, to);
			state.readTo = to;
		} catch (err) {
			console.error('[TourBooking] The tour dates couldn’t be read:', err);
			parts.dates.removeAttribute('aria-busy');
			parts.dates.replaceChildren();
			const retry = document.createElement('button');
			retry.type = 'button';
			retry.className = 'btn btn--sm btn--outline';
			retry.textContent = 'Try again';
			retry.addEventListener('click', () => load(), { once: true });
			parts.dates.append(retry);
			parts.whenNote.textContent = 'The tour dates couldn’t be loaded. Try again, or contact us to book.';
			if (retrying) {
				retry.focus();
			}
			return;
		}
		renderDates();
		renderWhenNote();
		showNextTour(state.tours);
		render();
		// after Try again, a keyboard's focus moves on to the first date it can pick
		if (retrying) {
			(parts.dates.querySelector('input:not(:disabled)') || parts.dates.querySelector('button'))?.focus();
		}
	};

	parts.dates.addEventListener('change', (event) => {
		if (event.target.name === 'tour-date') {
			pickDay(event.target.value);
		}
	});
	parts.dates.addEventListener('scroll', markMore, { passive: true });
	parts.times.addEventListener('change', (event) => {
		if (event.target.name === 'tour-time') {
			pickTour(event.target.value);
		}
	});
	parts.tickets.forEach((ticket) => {
		const change = (step) => {
			const count = Math.max(0, state.counts.get(ticket.variantId) + step);
			state.counts.set(ticket.variantId, count);
			showError('');
			render();
		};
		ticket.less.addEventListener('click', () => !isOff(ticket.less) && change(-1));
		ticket.more.addEventListener('click', () => !isOff(ticket.more) && change(1));
	});
	parts.submit.addEventListener('click', book);

	// The card's height, so on a screen shorter than the card it sticks by its foot, keeping Book now in view
	const holder = card.parentElement;
	if (holder && 'ResizeObserver' in window) {
		new ResizeObserver(() => {
			holder.style.setProperty('--tour-book-height', `${card.offsetHeight}px`);
		}).observe(card);
	}

	render();
	load();
}

/* ----------------------------------------------------------------------- */
/*  Next tour and the phone's bar                                          */
/* ----------------------------------------------------------------------- */

/**
 * The hero's Next tour: today, tomorrow or the date, and the time.
 *
 * @param {object[]} tours
 */
function showNextTour(tours) {
	const next = document.querySelector(SEL.next);
	const when = next?.querySelector(SEL.nextWhen);
	const tour = tours.find((each) => each.open);
	if (!next || !when || !tour) {
		return;
	}
	const now = today();
	let day = shortDate(new Date(tour.start));
	if (tour.day === now) {
		day = 'Today';
	} else if (tour.day === addDays(now, 1)) {
		day = 'Tomorrow';
	}
	when.textContent = tour.allDay ? day : `${day}, ${timeText(new Date(tour.start))}`;
	next.hidden = false;
}

/**
 * On a phone or tablet, the bar along the bottom shows once the hero, with its own Book now, has
 * scrolled away, and while the booking card is out of view.
 *
 * @param {HTMLElement} card
 */
function initPhoneBar(card) {
	const bar = document.querySelector(SEL.phoneBar);
	if (!bar || !('IntersectionObserver' in window)) {
		return;
	}
	const inView = new Set();
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				inView.add(entry.target);
			} else {
				inView.delete(entry.target);
			}
		});
		bar.hidden = inView.size > 0;
	});
	observer.observe(card);
	const hero = document.querySelector(SEL.hero);
	if (hero) {
		observer.observe(hero);
	}
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Start every booking card on the page, or in a section the theme editor has just drawn again.
 *
 * @param {ParentNode} [scope]
 */
export function initTourBooking(scope = document) {
	scope.querySelectorAll(SEL.card).forEach((card) => {
		createBooking(card);
		initPhoneBar(card);
	});
}

initTourBooking();
document.addEventListener('shopify:section:load', (event) => initTourBooking(event.target));

export { eventId, eventLabel };
